import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import AppLoader from '../app-loader'
import { isPublicRegistrationPath } from '../../utilits/public-registration-paths'
import { renderTenantNotFoundMarkup } from '../../../tenant-not-found/page'

type GuardState = 'checking' | 'ok' | 'invalid' | 'error'

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  font-size: 16px;
  line-height: 1.5;
  color: #333;
`

function isTenantMissing404(bodyText: string): boolean {
  if (bodyText.includes('Tenant is not configured')) return true
  try {
    const j = JSON.parse(bodyText) as { errors?: Array<{ detail?: string }> }
    return Boolean(
      j?.errors?.some(e => String(e?.detail || '').includes('Tenant is not configured')),
    )
  } catch {
    return false
  }
}

/**
 * Статика SPA отдаётся Traefik/nginx для любого *.base — без проверки тенанта.
 * Идеально: GET /api/health → 404 для неизвестного Host. На старых сборках /health
 * мог обходить резолвинг и отдавать 200 — тогда дублируем проверку через GET /api/session
 * (резолвинг там строгий; 404 с телом про тенант ≠ 404 без тела от loginRequired).
 */
export default function TenantHostGuard({ children }: { children: React.ReactNode }) {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const skipTenantCheck = isPublicRegistrationPath(pathname)
  const [state, setState] = useState<GuardState>(skipTenantCheck ? 'ok' : 'checking')

  useEffect(() => {
    if (skipTenantCheck) return

    let cancelled = false

    const run = async () => {
      try {
        const healthRes = await fetch('/api/health', {
          credentials: 'include',
          cache: 'no-store',
        })
        if (cancelled) return
        if (healthRes.status === 404) {
          setState('invalid')
          return
        }
        if (!healthRes.ok) {
          setState('error')
          return
        }

        const sessionRes = await fetch('/api/session', {
          credentials: 'include',
          cache: 'no-store',
        })
        if (cancelled) return
        if (sessionRes.status === 404) {
          const text = await sessionRes.text()
          if (isTenantMissing404(text)) {
            setState('invalid')
            return
          }
        }

        setState('ok')
      } catch {
        if (!cancelled) setState('error')
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [skipTenantCheck])

  if (state === 'checking') {
    return <AppLoader />
  }

  if (state === 'invalid') {
    // Та же страница, что отдаёт сервер (server.js) со статусом 404 — src/tenant-not-found/page.js
    const html = renderTenantNotFoundMarkup({
      host: typeof window !== 'undefined' ? window.location.host : '',
      authHost: process.env.REACT_APP_PLATFORM_AUTH_HOST || '',
    })
    return <div dangerouslySetInnerHTML={{ __html: html }} />
  }

  if (state === 'error') {
    return (
      <Wrap>
        <div>
          <h1 style={{ fontSize: 20, marginBottom: 12 }}>Не удалось проверить адрес</h1>
          <p>Повторите попытку позже или обратитесь к администратору.</p>
        </div>
      </Wrap>
    )
  }

  return <>{children}</>
}
